import { useState } from 'react'
import { colors, fonts } from './theme'
import SectionBar from './components/SectionBar'
import ProofBlock from './components/ProofBlock'

export default function RiskLab() {
  const [selectedView, setSelectedView] = useState('agent_model')

  const views = [
    { key: 'agent_model', label: 'Agent-Based Model' },
    { key: 'systemic_risk', label: 'Systemic Risk Map' },
    { key: 'stress_testing', label: 'Stress Testing' },
    { key: 'contagion', label: 'Contagion Channels' },
  ]

  return (
    <div style={styles.page}>
      <div style={styles.subNav}>
        {views.map(v => (
          <button
            key={v.key}
            onClick={() => setSelectedView(v.key)}
            style={{
              ...styles.subTab,
              ...(selectedView === v.key ? styles.subTabActive : {}),
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {selectedView === 'agent_model' && <AgentModelView />}
        {selectedView === 'systemic_risk' && <SystemicRiskView />}
        {selectedView === 'stress_testing' && <StressTestingView />}
        {selectedView === 'contagion' && <ContagionView />}
      </div>
    </div>
  )
}

function AgentModelView() {
  return (
    <>
      <SectionBar title="Doyne Farmer's Agent-Based Perspective" subtitle="Modeling private credit as a complex adaptive system" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>Why Agent-Based Modeling?</div>
        <p style={styles.conceptText}>
          J. Doyne Farmer (Santa Fe Institute, Oxford) argues that traditional equilibrium economics fails to
          capture the emergent behavior of financial systems. In private credit, the interactions between
          borrowers, lenders, CLO managers, rating agencies, repo counterparties, and regulators create
          feedback loops that cannot be understood by studying any single agent in isolation.
        </p>
        <p style={styles.conceptText}>
          An agent-based model (ABM) simulates each participant as an autonomous agent with its own
          decision rules, constraints, and information. System-level behavior (booms, crashes, contagion)
          emerges from the interaction of these agents — it is not imposed from above.
        </p>
      </div>

      <SectionBar title="The Agents in Private Credit" subtitle="Each with distinct incentives and constraints" />

      <div style={styles.agentGrid}>
        <AgentCard
          name="PE Sponsor"
          behavior="Maximizes equity IRR via leverage. Buys companies, loads them with debt, extracts dividends. Rational agent with information advantage over lenders."
          constraints={['Fund lifecycle (5-7yr deployment)', 'LP return expectations (20%+ IRR)', 'Reputation / repeat player dynamics']}
          feedbackLoop="Higher CLO demand → cheaper loans → more leverage → more buyouts → more loans → more CLO demand"
        />
        <AgentCard
          name="Direct Lender / BDC"
          behavior="Originates loans to PE-backed companies. Earns spread over cost of capital. Must maintain portfolio quality for shareholders and regulators."
          constraints={['Regulatory leverage (2:1 debt/equity)', 'Dividend coverage requirement', 'Credit committee approval process']}
          feedbackLoop="More AUM → more deployment pressure → reach for yield → lower credit quality → higher future defaults"
        />
        <AgentCard
          name="CLO Manager"
          behavior="Selects loans, manages portfolio, trades actively during reinvestment period. Earns management fee + incentive fee on equity returns."
          constraints={['OC/IC test compliance', 'CCC bucket limits', 'Reinvestment period window', 'Rating agency criteria']}
          feedbackLoop="Good performance → easier fundraising → more CLOs → more loan demand → tighter spreads → lower future returns"
        />
        <AgentCard
          name="Rating Agency"
          behavior="Assigns ratings to CLO tranches based on models of default/recovery. Downgrades trigger forced selling and OC test failures."
          constraints={['Model-driven (historical default/recovery data)', 'Reputation risk from incorrect ratings', 'Issuer-pays conflict of interest']}
          feedbackLoop="Downgrades → forced selling → price declines → mark-to-market losses → more downgrades (pro-cyclical)"
        />
        <AgentCard
          name="Repo Counterparty (Bank)"
          behavior="Provides secured funding against CLO AAA notes. Earns financing spread. Manages risk via haircuts and margin calls."
          constraints={['Basel III capital requirements', 'Liquidity Coverage Ratio (LCR)', 'Internal risk limits / VaR']}
          feedbackLoop="Stress → increase haircuts → forced selling → price declines → higher haircuts (margin spiral)"
        />
        <AgentCard
          name="Borrower (Portfolio Company)"
          behavior="Operates business, services debt, tries to grow. Behavior changes under stress — may cut investment, lay off workers, or default."
          constraints={['Cash flow / EBITDA', 'Covenant compliance', 'Competitive dynamics', 'Management incentives']}
          feedbackLoop="Rate increases → higher debt service → less investment → lower growth → lower EBITDA → covenant breach"
        />
      </div>

      <SectionBar title="Emergent Properties" subtitle="What the system does that no single agent intends" />

      <div style={styles.emergentGrid}>
        <EmergentCard
          title="Credit Cycle Amplification"
          desc="In booms, all agents act rationally: sponsors buy more, lenders compete to deploy, CLOs bid aggressively. Each agent's actions make the others' actions more profitable. The system overshoots because no single agent has a reason to stop. In busts, the reverse: everyone de-risks simultaneously, amplifying the downturn."
        />
        <EmergentCard
          title="Herding via Benchmarking"
          desc="CLO managers are benchmarked against peers. This creates herding: if competitors are buying a loan, you need to buy it to maintain relative performance. If competitors are selling, you need to sell. The result is artificial correlation — loans move together not because of fundamental linkage but because the same agents own all of them."
        />
        <EmergentCard
          title="Information Asymmetry Cascades"
          desc="Private credit is opaque by design. When one lender discovers a problem with a borrower, they may sell quietly. Other lenders see the selling, infer something is wrong, and sell too — even without knowing the original information. The signal propagates faster than the information."
        />
      </div>
    </>
  )
}

function SystemicRiskView() {
  return (
    <>
      <SectionBar title="Systemic Risk Map" subtitle="How risks propagate through private credit" accent="gold" />

      <div style={styles.riskMap}>
        <RiskNode
          title="Macroeconomic Shock"
          level="trigger"
          desc="Recession, rate spike, or credit event triggers initial stress on borrowers"
          connections={['Borrower Distress', 'Market Volatility']}
        />
        <RiskNode
          title="Borrower Distress"
          level="primary"
          desc="Revenue declines, EBITDA drops, covenant breaches, payment defaults"
          connections={['BDC/Lender Losses', 'CLO Portfolio Deterioration']}
        />
        <RiskNode
          title="CLO Portfolio Deterioration"
          level="primary"
          desc="Loan downgrades, CCC bucket breaches, OC test failures"
          connections={['Forced CLO Selling', 'Rating Agency Downgrades', 'Reduced CLO Issuance']}
        />
        <RiskNode
          title="Rating Agency Downgrades"
          level="amplifier"
          desc="CLO tranche downgrades trigger forced selling by insurance companies, banks with regulatory constraints"
          connections={['Forced CLO Selling', 'Repo Margin Calls']}
        />
        <RiskNode
          title="Forced CLO Selling"
          level="amplifier"
          desc="CLO managers forced to sell loans to cure OC tests; downgraded CLO holders forced to sell tranches"
          connections={['Loan Price Declines', 'Market Volatility']}
        />
        <RiskNode
          title="Repo Margin Calls"
          level="amplifier"
          desc="Banks increase haircuts on CLO repo, call margin from leveraged CLO AAA holders"
          connections={['Forced CLO Selling', 'Bank Balance Sheet Stress']}
        />
        <RiskNode
          title="Loan Price Declines"
          level="secondary"
          desc="Forced selling depresses loan prices, even for performing loans. Mark-to-market losses spread."
          connections={['More OC Test Failures', 'Reduced New Lending']}
        />
        <RiskNode
          title="Reduced New Lending"
          level="secondary"
          desc="BDCs and CLOs pull back from new lending. PE deal activity collapses. Refinancing becomes difficult."
          connections={['More Borrower Distress', 'PE Deal Activity Collapse']}
        />
      </div>

      <SectionBar title="Historical Stress Episodes" subtitle="What actually happened" />

      <div style={styles.episodesGrid}>
        <EpisodeCard
          year="2008-2009"
          event="Global Financial Crisis"
          impact="CLO issuance collapsed from $91B (2007) to $12B (2009). Leveraged loan prices fell to 60 cents on the dollar. But CLO AAA tranches had 0% realized losses. The structure held despite extreme stress."
          lesson="Subordination works. Active management helped CLO managers trade out of the worst credits."
        />
        <EpisodeCard
          year="2015-2016"
          event="Energy/Commodity Crash"
          impact="Oil & gas CLO exposure caused CCC buckets to swell. Several CLOs failed OC tests. Energy loan prices dropped 30-40%. Losses concentrated in CLO equity and junior mezz."
          lesson="Sector concentration risk. CLOs with diversified portfolios survived; those overweight energy suffered."
        />
        <EpisodeCard
          year="2020"
          event="COVID-19"
          impact="Leveraged loan prices dropped to 76 cents in March, recovered to 95+ by August. Fastest credit cycle in history. CLO new issuance paused for ~6 weeks then resumed at record pace."
          lesson="Speed of recovery prevented structural damage. But revealed how much private credit depends on broad economic stability."
        />
        <EpisodeCard
          year="2022-2023"
          event="Rate Shock"
          impact="SOFR went from 0% to 5%+ in 18 months. Floating-rate borrowers saw debt service costs double. Default rates rose from 1% to 3%. But BDC NII surged — the biggest beneficiaries of rising rates."
          lesson="Private credit's floating-rate nature is a double-edged sword: lenders benefit, borrowers suffer."
        />
      </div>
    </>
  )
}

function StressTestingView() {
  return (
    <>
      <SectionBar title="Stress Test Scenarios" subtitle="Build your own what-if analysis" accent="gold" />

      <div style={styles.conceptBlock}>
        <div style={styles.conceptTitle}>Framework: Scenario-Based Stress Testing</div>
        <p style={styles.conceptText}>
          True stress testing — in the Ackman tradition — means constructing scenarios so bearish they make you
          uncomfortable, then rigorously modeling the impact. The goal is not to predict the future, but to
          understand the conditions under which your thesis breaks.
        </p>
      </div>

      <SectionBar title="Scenario Library" />

      <div style={styles.scenarioGrid}>
        <ScenarioCard
          name="Base Case"
          probability="60%"
          assumptions={[
            'SOFR normalizes to 3-3.5% by 2027',
            'Default rate: 2-3% (historical average)',
            'CLO issuance: $130-150B/year',
            'BDC NII covers dividends with 5-10% cushion',
          ]}
          impact="BDC total returns: 10-12%. CLO equity: 12-15%. Steady-state private credit growth."
          color={colors.greenInk}
        />
        <ScenarioCard
          name="Mild Recession"
          probability="25%"
          assumptions={[
            'GDP contracts 1-2%, unemployment rises to 5.5%',
            'Default rate: 4-6%',
            'Leveraged loan prices fall 10-15%',
            'CLO issuance slows 30-40%',
          ]}
          impact="BDC NAV declines 5-10%. Some dividend cuts. CLO equity returns turn negative. BBB/BB CLO tranches face mark-to-market losses."
          color={colors.amberInk}
        />
        <ScenarioCard
          name="Severe Recession + Rate Shock"
          probability="10%"
          assumptions={[
            'GDP contracts 3-4%, unemployment hits 8%+',
            'Default rate: 8-12%',
            'SOFR stays elevated (stagflation)',
            'Leveraged loan prices fall 25-35%',
          ]}
          impact="BDC NAV declines 20-30%. Widespread dividend cuts. CLO OC test failures cascade. CLO equity wiped out. AAA still protected by 35%+ subordination."
          color={colors.redInk}
        />
        <ScenarioCard
          name="Tail Risk: Private Credit Crisis"
          probability="5%"
          assumptions={[
            'Major PE-backed defaults cluster (correlated risk)',
            'CLO AAA repo market freezes',
            'Rating agencies downgrade across the board',
            'Regulatory intervention / lending restrictions',
          ]}
          impact="Systemic stress. BDCs trade at 50-60% of NAV. CLO market effectively closed for 6-12 months. Recovery takes 2-3 years. But: AAA tranches still likely to get par at maturity."
          color={colors.redInk}
        />
      </div>

      <SectionBar title="Build Your Own Stress Test" subtitle="Coming soon — interactive scenario builder" />

      <div style={styles.comingSoon}>
        <p style={styles.comingSoonText}>
          Interactive stress test builder with adjustable parameters: default rate, recovery rate, SOFR path,
          CLO issuance pace, repo haircut levels, and rating agency behavior. Model the cascade effects
          across the entire private credit ecosystem using the agent-based framework.
        </p>
        <div style={styles.comingSoonBadge}>Under Construction</div>
      </div>
    </>
  )
}

function ContagionView() {
  return (
    <>
      <SectionBar title="Contagion Channels" subtitle="How stress propagates between agents" accent="gold" />

      <div style={styles.channelsGrid}>
        <ChannelCard
          from="Borrower Default"
          to="CLO OC Test"
          mechanism="A defaulted loan is marked to recovery value (typically 40-70 cents). This reduces the numerator of the OC ratio. If enough loans default, the OC test fails."
          severity="high"
          speed="Weeks to months"
        />
        <ChannelCard
          from="CLO OC Failure"
          to="CLO Equity"
          mechanism="When OC test fails, cash flows are diverted from equity to pay down senior tranches. Equity distributions stop. If prolonged, equity tranche is permanently impaired."
          severity="high"
          speed="Immediate"
        />
        <ChannelCard
          from="Rating Downgrade"
          to="Insurance Company"
          mechanism="Insurance regulators require risk-based capital charges. A CLO tranche downgrade from AA to BBB can increase capital charges 5-10x, forcing the insurer to sell."
          severity="medium"
          speed="Days to weeks"
        />
        <ChannelCard
          from="Repo Haircut Increase"
          to="Bank Leverage"
          mechanism="When repo lenders increase haircuts (e.g., from 5% to 15%), leveraged CLO AAA holders must post additional margin or sell. This forces selling into a declining market."
          severity="critical"
          speed="Hours to days"
        />
        <ChannelCard
          from="CLO Issuance Freeze"
          to="PE Deal Activity"
          mechanism="If CLOs stop issuing (no new tranches), demand for leveraged loans collapses. Banks can't syndicate PE acquisition financing. PE deal activity freezes."
          severity="high"
          speed="Weeks"
        />
        <ChannelCard
          from="PE Deal Freeze"
          to="CLO Reinvestment"
          mechanism="Without new PE deals, fewer new loans are originated. CLO managers in reinvestment mode can't find new loans to buy. Loan repayments flow through as amortization, reducing CLO size."
          severity="medium"
          speed="Months"
        />
        <ChannelCard
          from="Loan Price Declines"
          to="BDC NAV"
          mechanism="BDCs mark their portfolios to fair value quarterly. Even performing loans get marked down if secondary market prices decline. NAV drops trigger investor selling, potentially widening discounts."
          severity="medium"
          speed="Quarterly"
        />
        <ChannelCard
          from="BDC Price Decline"
          to="New Capital Formation"
          mechanism="BDCs trading at discounts to NAV cannot raise equity (dilutive). Without new equity, BDCs can't grow or support new lending. Reduces capital available to middle-market borrowers."
          severity="medium"
          speed="Months to quarters"
        />
      </div>
    </>
  )
}

// Sub-components
function AgentCard({ name, behavior, constraints, feedbackLoop }) {
  return (
    <div style={styles.agentCard}>
      <div style={styles.agentName}>{name}</div>
      <div style={styles.agentSection}>
        <span style={styles.agentLabel}>Behavior:</span>
        <span style={styles.agentText}>{behavior}</span>
      </div>
      <div style={styles.agentSection}>
        <span style={styles.agentLabel}>Constraints:</span>
        {constraints.map((c, i) => (
          <div key={i} style={styles.agentConstraint}>{c}</div>
        ))}
      </div>
      <div style={{ ...styles.agentSection, borderTop: `1px solid ${colors.rule}`, paddingTop: 8, marginTop: 4 }}>
        <span style={{ ...styles.agentLabel, color: colors.amberInk }}>Feedback Loop:</span>
        <span style={styles.agentText}>{feedbackLoop}</span>
      </div>
    </div>
  )
}

function EmergentCard({ title, desc }) {
  return (
    <div style={styles.emergentCard}>
      <div style={styles.emergentTitle}>{title}</div>
      <div style={styles.emergentDesc}>{desc}</div>
    </div>
  )
}

function RiskNode({ title, level, desc, connections }) {
  const levelColors = {
    trigger: colors.redInk,
    primary: colors.amberInk,
    amplifier: colors.redInk,
    secondary: colors.steel,
  }

  return (
    <div style={{ ...styles.riskNode, borderLeftColor: levelColors[level] || colors.textMuted }}>
      <div style={styles.riskNodeHeader}>
        <span style={{ ...styles.riskNodeTitle, color: levelColors[level] }}>{title}</span>
        <span style={{ ...styles.riskNodeLevel, color: levelColors[level] }}>{level.toUpperCase()}</span>
      </div>
      <div style={styles.riskNodeDesc}>{desc}</div>
      <div style={styles.riskNodeConnections}>
        Propagates to: {connections.map((c, i) => (
          <span key={i} style={styles.riskNodeConnection}>
            {c}{i < connections.length - 1 ? ' → ' : ''}
          </span>
        ))}
      </div>
    </div>
  )
}

function EpisodeCard({ year, event, impact, lesson }) {
  return (
    <div style={styles.episodeCard}>
      <div style={styles.episodeYear}>{year}</div>
      <div style={styles.episodeEvent}>{event}</div>
      <div style={styles.episodeImpact}>{impact}</div>
      <div style={styles.episodeLesson}><strong>Lesson:</strong> {lesson}</div>
    </div>
  )
}

function ScenarioCard({ name, probability, assumptions, impact, color }) {
  return (
    <div style={{ ...styles.scenarioCard, borderTopColor: color }}>
      <div style={styles.scenarioHeader}>
        <span style={{ ...styles.scenarioName, color }}>{name}</span>
        <span style={{ ...styles.scenarioProb, color }}>{probability}</span>
      </div>
      <div style={styles.scenarioSection}>
        <span style={styles.scenarioLabel}>Assumptions:</span>
        {assumptions.map((a, i) => (
          <div key={i} style={styles.scenarioItem}>{a}</div>
        ))}
      </div>
      <div style={styles.scenarioSection}>
        <span style={styles.scenarioLabel}>Impact:</span>
        <div style={styles.scenarioImpact}>{impact}</div>
      </div>
    </div>
  )
}

function ChannelCard({ from, to, mechanism, severity, speed }) {
  const sevColor = severity === 'critical' ? colors.redInk : severity === 'high' ? colors.amberInk : colors.steel

  return (
    <div style={styles.channelCard}>
      <div style={styles.channelRoute}>
        <span style={styles.channelFrom}>{from}</span>
        <span style={styles.channelArrow}>→</span>
        <span style={styles.channelTo}>{to}</span>
      </div>
      <div style={styles.channelMechanism}>{mechanism}</div>
      <div style={styles.channelMeta}>
        <span style={{ ...styles.channelSeverity, color: sevColor }}>Severity: {severity.toUpperCase()}</span>
        <span style={styles.channelSpeed}>Speed: {speed}</span>
      </div>
    </div>
  )
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  subNav: {
    display: 'flex',
    gap: 2,
    padding: '8px 32px',
    borderBottom: `1px solid ${colors.rule}`,
    background: colors.navyDeep,
  },
  subTab: {
    padding: '4px 14px',
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 500,
    color: colors.textMuted,
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 2,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  subTabActive: {
    color: colors.gold,
    borderColor: colors.goldBorder,
    background: colors.goldBg,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  conceptBlock: {
    padding: 20,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  conceptTitle: {
    fontFamily: fonts.serif,
    fontSize: 16,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 10,
  },
  conceptText: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.7,
    marginBottom: 8,
  },
  // Agent grid
  agentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 12,
  },
  agentCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  agentName: {
    fontFamily: fonts.serif,
    fontSize: 16,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.rule}`,
  },
  agentSection: {
    marginBottom: 8,
  },
  agentLabel: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    color: colors.gold,
    display: 'block',
    marginBottom: 4,
  },
  agentText: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
  agentConstraint: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    padding: '2px 0',
    paddingLeft: 12,
  },
  // Emergent
  emergentGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  emergentCard: {
    padding: 16,
    background: colors.navyDeep,
    border: `1px solid ${colors.goldBorder}`,
    borderLeft: `3px solid ${colors.gold}`,
    borderRadius: 3,
  },
  emergentTitle: {
    fontFamily: fonts.serif,
    fontSize: 15,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 8,
  },
  emergentDesc: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.7,
  },
  // Risk map
  riskMap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  riskNode: {
    padding: '12px 16px',
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderLeft: '3px solid',
    borderRadius: 3,
  },
  riskNodeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  riskNodeTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
  },
  riskNodeLevel: {
    fontFamily: fonts.mono,
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 2,
    opacity: 0.7,
  },
  riskNodeDesc: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 6,
  },
  riskNodeConnections: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textMuted,
  },
  riskNodeConnection: {
    color: colors.steel,
  },
  // Episodes
  episodesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 12,
  },
  episodeCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  episodeYear: {
    fontFamily: fonts.mono,
    fontSize: 20,
    fontWeight: 700,
    color: colors.gold,
    marginBottom: 2,
  },
  episodeEvent: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 10,
  },
  episodeImpact: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.6,
    marginBottom: 8,
  },
  episodeLesson: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.greenInk,
    lineHeight: 1.5,
    padding: '6px 10px',
    background: colors.greenBg,
    borderRadius: 2,
  },
  // Scenarios
  scenarioGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 12,
  },
  scenarioCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderTop: '3px solid',
    borderRadius: 3,
  },
  scenarioHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  scenarioName: {
    fontFamily: fonts.serif,
    fontSize: 16,
    fontWeight: 600,
  },
  scenarioProb: {
    fontFamily: fonts.mono,
    fontSize: 14,
    fontWeight: 600,
  },
  scenarioSection: {
    marginBottom: 10,
  },
  scenarioLabel: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    color: colors.textMuted,
    display: 'block',
    marginBottom: 4,
  },
  scenarioItem: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    padding: '2px 0',
    paddingLeft: 10,
  },
  scenarioImpact: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.5,
  },
  // Coming soon
  comingSoon: {
    padding: 24,
    background: colors.navySurface,
    border: `1px dashed ${colors.steelBorder}`,
    borderRadius: 3,
    textAlign: 'center',
  },
  comingSoonText: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textMuted,
    lineHeight: 1.6,
    marginBottom: 12,
  },
  comingSoonBadge: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
    color: colors.steel,
    padding: '4px 12px',
    border: `1px solid ${colors.steelBorder}`,
    borderRadius: 2,
    display: 'inline-block',
  },
  // Channels
  channelsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 12,
  },
  channelCard: {
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  channelRoute: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.rule}`,
  },
  channelFrom: {
    fontFamily: fonts.mono,
    fontSize: 12,
    fontWeight: 600,
    color: colors.redInk,
  },
  channelArrow: {
    color: colors.textMuted,
    fontSize: 14,
  },
  channelTo: {
    fontFamily: fonts.mono,
    fontSize: 12,
    fontWeight: 600,
    color: colors.steel,
  },
  channelMechanism: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.6,
    marginBottom: 10,
  },
  channelMeta: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  channelSeverity: {
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 600,
  },
  channelSpeed: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.textMuted,
  },
}
